import { HeaderCard } from '@data/components/composite/HeaderCard/HeaderCard';
import { Metrics } from '@data/components/composite/Metrics/Metrics';
import { getSchoolYearDateRange } from '@data/utils/get-school-year';
import { format } from 'date-fns';
import { useGetMeApi } from 'eds-common/api/v2';
import { useMemo } from 'react';
import { ProductCard } from '@data/components/composite/ProductCard/ProductCard';
import { isSupportedProduct } from '@data/config/reports';
import { useApplications } from '@data/hooks/api/use-applications-api/use-applications-api';
import { getGradeDisplay } from '@data/utils/get-grade-range';
import { getEdApps } from '@data/utils/get-ed-apps';
import {
  selectEdAppMetrics,
  useMetricsSessionsV2Api,
} from '@data/hooks/api/use-metrics-sessions-v2-api/use-metrics-sessions-v2-api';

export function ProductLandingPage() {
  const { data: user } = useGetMeApi();
  const { data: applications } = useApplications();

  const supportedApps = applications?.filter((app) =>
    isSupportedProduct(app.applicationCode)
  );

  const appCodes = useMemo(
    () => supportedApps?.map((app) => app.applicationCode) ?? [],
    [supportedApps]
  );

  const { from: startDate, to: endDate } = useMemo(
    () => getSchoolYearDateRange(),
    []
  );

  const metricsEnabled = !!user?.userId && !!user?.currentOrg?.orgId;

  const edApps = useMemo(() => getEdApps(appCodes), [appCodes]);

  // One request covers the whole page: the Highlights read the roll-up across
  // every product, each card reads its own edApp out of the same response. No
  // card waits on another request, so they all resolve together.
  const {
    data: metricsData,
    isLoading: metricsLoading,
    isFetched: metricsFetched,
  } = useMetricsSessionsV2Api({
    orgId: user?.currentOrg?.orgId ?? '',
    teacherIds: [user?.userId ?? ''],
    startDate,
    endDate,
    edApps,
    // Waiting for the product list keeps this to a single request: edApps is
    // empty until /composite/applications resolves, and the service rejects an
    // empty filter anyway.
    enabled: metricsEnabled && edApps.length > 0,
  });

  const metrics = metricsData?.totalResult ?? [];

  return (
    <div className="flex-1 bg-brand-grey_7 antialiased">
      <div className="mx-auto flex w-full max-w-[1440px] flex-col py-6 mobile:px-4 tablet:px-10">
        <HeaderCard>
          <h2>Data and Insights</h2>
        </HeaderCard>
        {applications && supportedApps?.length === 0 ? (
          <div className="rounded-lg border-[1px] border-dashed border-neutral-400 px-6 py-8 tablet:mt-7 tablet:p-10">
            <h3 className="text-xl font-bold tablet:text-2xl">
              No supported products yet
            </h3>
            <p className="mt-2 text-sm text-neutral-1300 tablet:text-lg">
              Data &amp; Insights are unavailable for your current products.
            </p>
          </div>
        ) : (
          <>
            <h2 className="text-2xl font-semibold">Highlights</h2>
            <p className="mt-3 pb-3 text-sm text-neutral-1300">
              Student data for {format(startDate, 'MM/dd/yyyy')} -{' '}
              {format(endDate, 'MM/dd/yyyy')}
            </p>
            <Metrics metrics={metrics} isLoading={metricsLoading} count={2} />
            <h2 className="mb-4 mt-8 text-2xl font-semibold">
              Available Products
              {supportedApps ? ` (${supportedApps.length})` : ''}
            </h2>
            <div className="grid grid-cols-1 gap-4 tablet:grid-cols-2 tablet:gap-8 laptop:grid-cols-3">
              {supportedApps?.map((app) => {
                return (
                  <ProductCard
                    key={app.applicationCode}
                    applicationCode={app.applicationCode}
                    applicationLabel={app.applicationLabel}
                    applicationIconSrc={app.applicationIconSrc}
                    gradeDisplay={getGradeDisplay(app)}
                    sessionMetrics={{
                      values: selectEdAppMetrics(
                        metricsData,
                        app.applicationCode
                      ),
                      isLoading: !metricsFetched,
                    }}
                  />
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
